/**
 * WAF Rules — per-subdomain view of the domain's managed CRS (Core Rule Set)
 * rules, split into 3 category tabs: XSS / SQL Injection / Generic Injection.
 * Each rule can be toggled on/off and have its action (block / simulate / pass)
 * changed, both per-row and in bulk across selected rows. All writes go through
 * the single bulk PATCH (`useUpdateWafRules`) — a per-row change is just a
 * one-element `rule_ids`.
 */
import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, RefreshCw } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination, type ColumnDef } from '@vnetwork/ui/data';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Switch,
  Button,
  Input,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import {
  useWafRuleOptions,
  useWafRules,
  useUpdateWafRules,
  wafExtractError,
  type WafRule,
  type WafRuleAction,
  type WafRuleCategory,
  type WafRuleOption,
} from '@/api/waf';
import { trackFeatureAction } from '@/lib/analytics-events';
import { WafScopeBar, type WafScope } from '../analytics/WafScopeBar';
import { loadJSON, saveJSON } from '../analytics/storage';
import styles from './WafCrsRules.module.css';

const CATEGORY_TABS: { value: WafRuleCategory; label: string }[] = [
  { value: 'xss', label: 'XSS' },
  { value: 'sql_injection', label: 'SQL Injection' },
  { value: 'generic_injection', label: 'Generic Injection' },
];

const ACTION_FALLBACK: WafRuleOption[] = [
  { value: 'block', label: 'Block' },
  { value: 'simulate', label: 'Simulate' },
  { value: 'pass', label: 'Pass' },
];

const ALL_ACTIONS = '__all__';
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Per-row enable Switch — optimistic, reverts on error. */
function RuleEnableCell({
  serviceId,
  domain,
  rule,
}: {
  serviceId: string;
  domain: string;
  rule: WafRule;
}) {
  const { show } = useToast();
  const [on, setOn] = useState(rule.enabled);
  useEffect(() => setOn(rule.enabled), [rule.enabled]);
  const update = useUpdateWafRules(serviceId, {
    onSuccess: (_d, vars) => {
      show({ tone: 'success', title: vars.input.enabled ? 'Rule enabled' : 'Rule disabled' });
      trackFeatureAction('waf', 'crs_rule', vars.input.enabled ? 'enable' : 'disable');
    },
    onError: (e) => { setOn(rule.enabled); show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }); },
  });
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }}>
      <Switch
        checked={on}
        aria-label="Enabled"
        disabled={update.isPending}
        onCheckedChange={(v) => { setOn(v); update.mutate({ domain, input: { rule_ids: [rule.id], enabled: v } }); }}
      />
    </div>
  );
}

/** Per-row action Select — optimistic, reverts on error. */
function RuleActionCell({
  serviceId,
  domain,
  rule,
  actions,
}: {
  serviceId: string;
  domain: string;
  rule: WafRule;
  actions: WafRuleOption[];
}) {
  const { show } = useToast();
  const [act, setAct] = useState<WafRuleAction>(rule.action);
  useEffect(() => setAct(rule.action), [rule.action]);
  const update = useUpdateWafRules(serviceId, {
    onSuccess: () => { show({ tone: 'success', title: 'Rule action updated' }); trackFeatureAction('waf', 'crs_rule', 'edit'); },
    onError: (e) => { setAct(rule.action); show({ tone: 'danger', title: wafExtractError(e, 'Update failed') }); },
  });
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Select
        value={act}
        disabled={update.isPending}
        onValueChange={(v) => {
          setAct(v as WafRuleAction);
          update.mutate({ domain, input: { rule_ids: [rule.id], action: v as WafRuleAction } });
        }}
      >
        <SelectTrigger aria-label="Action" className={styles.rowSelect}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {actions.map((a) => (
            <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** The tabs + toolbar + bulk bar + table for a resolved domain. */
function RulesBody({ serviceId, domain }: { serviceId: string; domain: string }) {
  const { show } = useToast();
  const tabKey = `waf:${serviceId}:rules:tab`;
  const [tab, setTab] = useState<WafRuleCategory>(() => loadJSON<WafRuleCategory>(tabKey, 'xss'));
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const optionsQ = useWafRuleOptions(serviceId);
  const actions = optionsQ.data?.actions?.length ? optionsQ.data.actions : ACTION_FALLBACK;
  const categoryLabel = useMemo(() => {
    const m = new Map((optionsQ.data?.categories ?? []).map((c) => [c.value, c.label]));
    return (cat: WafRuleCategory, fallback: string) => m.get(cat) ?? fallback;
  }, [optionsQ.data]);

  const rulesQ = useWafRules(serviceId, domain, {
    category: tab,
    action: (actionFilter as WafRuleAction) || undefined,
  });
  const rules = useMemo(() => rulesQ.data ?? [], [rulesQ.data]);

  // Reset the selection whenever the tab or domain changes.
  useEffect(() => setRowSelection({}), [tab, domain]);

  const changeTab = (t: WafRuleCategory) => { setTab(t); saveJSON(tabKey, t); };

  // Client-side text search over id / message.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter((r) => String(r.id).includes(q) || r.message.toLowerCase().includes(q));
  }, [rules, search]);

  // Client-side pagination (the listing isn't paginated server-side).
  useEffect(() => setPage(1), [tab, search, actionFilter]);
  const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
  const curPage = Math.min(page, pageCount);
  const paged = useMemo(
    () => visible.slice((curPage - 1) * pageSize, curPage * pageSize),
    [visible, curPage, pageSize],
  );

  const selectedIds = useMemo(
    () => Object.entries(rowSelection).filter(([, v]) => v).map(([k]) => Number(k)),
    [rowSelection],
  );

  const bulk = useUpdateWafRules(serviceId, {
    onSuccess: () => { setRowSelection({}); show({ tone: 'success', title: 'Rules updated' }); trackFeatureAction('waf', 'crs_rule', 'edit'); },
    onError: (e) => show({ tone: 'danger', title: wafExtractError(e, 'Bulk update failed') }),
  });
  const applyBulk = (input: { enabled?: boolean; action?: WafRuleAction }) =>
    bulk.mutate({ domain, input: { rule_ids: selectedIds, ...input } });

  const columns = useMemo<ColumnDef<WafRule, unknown>[]>(
    () => [
      {
        id: 'id',
        accessorKey: 'id',
        header: 'Rule ID',
        cell: ({ row }) => <span className={styles.idCell}>{row.original.id}</span>,
      },
      {
        id: 'message',
        accessorKey: 'message',
        header: 'Description',
        enableSorting: false,
        cell: ({ row }) => row.original.message || <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>,
      },
      {
        id: 'action',
        header: 'Action',
        enableSorting: false,
        cell: ({ row }) => (
          <RuleActionCell serviceId={serviceId} domain={domain} rule={row.original} actions={actions} />
        ),
      },
      {
        id: 'enabled',
        header: 'Enabled',
        enableSorting: false,
        cell: ({ row }) => <RuleEnableCell serviceId={serviceId} domain={domain} rule={row.original} />,
      },
    ],
    [serviceId, domain, actions],
  );

  return (
    <div>
      <Tabs value={tab} onValueChange={(v) => changeTab(v as WafRuleCategory)}>
        <TabsList>
          {CATEGORY_TABS.map((c) => (
            <TabsTrigger key={c.value} value={c.value}>{categoryLabel(c.value, c.label)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className={styles.toolbar}>
        <Input
          className={styles.search}
          placeholder="Search by ID or description…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={actionFilter || ALL_ACTIONS} onValueChange={(v) => setActionFilter(v === ALL_ACTIONS ? '' : v)}>
          <SelectTrigger aria-label="Filter by action" className={styles.actionFilter}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_ACTIONS}>All actions</SelectItem>
            {actions.map((a) => (
              <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant="secondary"
          startIcon={<RefreshCw size={15} className={rulesQ.isFetching ? styles.spin : undefined} />}
          disabled={rulesQ.isFetching}
          onClick={() => rulesQ.refetch()}
        >
          Refresh
        </Button>
      </div>

      {selectedIds.length > 0 && (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedIds.length} selected</span>
          <Button size="sm" variant="secondary" disabled={bulk.isPending} onClick={() => applyBulk({ enabled: true })}>
            Enable
          </Button>
          <Button size="sm" variant="secondary" disabled={bulk.isPending} onClick={() => applyBulk({ enabled: false })}>
            Disable
          </Button>
          <Select value="" disabled={bulk.isPending} onValueChange={(v) => applyBulk({ action: v as WafRuleAction })}>
            <SelectTrigger aria-label="Set action" className={styles.rowSelect}>
              <SelectValue placeholder="Set action…" />
            </SelectTrigger>
            <SelectContent>
              {actions.map((a) => (
                <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className={styles.bulkSpacer} />
          <Button size="sm" variant="ghost" onClick={() => setRowSelection({})}>Clear</Button>
        </div>
      )}

      {rulesQ.isPending ? (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} style={{ height: 44 }} />)}
        </div>
      ) : rulesQ.isError ? (
        <Alert tone="danger" title="Couldn't load CRS rules" actions={<button onClick={() => rulesQ.refetch()}>Retry</button>}>
          {wafExtractError(rulesQ.error, "This domain's CRS rules failed to load.")}
        </Alert>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={paged}
            getRowId={(r) => String(r.id)}
            enableSelection
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            emptyState={search ? 'No rules match your search.' : 'No rules in this category.'}
            footer={`Showing ${visible.length ? (curPage - 1) * pageSize + 1 : 0}–${(curPage - 1) * pageSize + paged.length} of ${visible.length} rule${visible.length === 1 ? '' : 's'}`}
          />
          {(pageCount > 1 || visible.length > PAGE_SIZE_OPTIONS[0]) && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--vn-space-4)',
                marginTop: 'var(--vn-space-4)',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                <label htmlFor="waf-rules-page-size" style={{ color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
                  Rows per page
                </label>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger id="waf-rules-page-size" size="sm" style={{ width: 72 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pageCount > 1 && (
                <Pagination
                  page={curPage}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  summary={`Page ${curPage} of ${pageCount}`}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function WafCrsRules({ serviceId }: { serviceId: string }) {
  const [scope, setScope] = useState<WafScope>({});
  const domain = scope.subdomain;
  const noSubdomain = !!scope.zone && !domain;

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title="CRS Rules"
        description="Tune the managed Core Rule Set (CRS) for the selected subdomain — enable/disable rules and set their action across XSS, SQL Injection and Generic Injection."
      />

      <div className={styles.scopeBar}>
        <WafScopeBar serviceId={serviceId} mode="subdomain" onChange={setScope} />
      </div>

      {noSubdomain ? (
        <EmptyState
          icon={<ShieldCheck size={20} />}
          title="No proxied subdomains"
          description="This zone has no proxied subdomain to manage CRS rules for. Proxy a record first."
        />
      ) : !domain ? (
        <EmptyState
          icon={<ShieldCheck size={20} />}
          title="Select a subdomain"
          description="Pick a domain and subdomain above to manage its CRS rules."
        />
      ) : (
        <RulesBody key={domain} serviceId={serviceId} domain={domain} />
      )}
    </Container>
  );
}

export default WafCrsRules;
